import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useSearchParams } from 'react-router-dom'
import { RefreshCw, X, Sparkles, Layers, Wand2, Film, User, BookOpen } from 'lucide-react'
import { api } from '@/lib/api'
import { formatDateTime, getStatusColor } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { toast } from '@/components/ui/toaster'
import type { TaskType } from '@/types'

function getTaskIconConfig(taskType: TaskType) {
  switch (taskType) {
    case 'competitor_research':
      return {
        icon: <Sparkles className="h-5 w-5 text-spectrum-teal" />,
        bgColor: 'bg-spectrum-teal/10'
      }
    case 'variation':
      return {
        icon: <Layers className="h-5 w-5 text-spectrum-cyan" />,
        bgColor: 'bg-spectrum-cyan/10'
      }
    case 'image_freestyle':
      return {
        icon: <Wand2 className="h-5 w-5 text-spectrum-blue" />,
        bgColor: 'bg-spectrum-blue/10'
      }
    case 'video_freestyle':
      return {
        icon: <Film className="h-5 w-5 text-spectrum-purple" />,
        bgColor: 'bg-spectrum-purple/10'
      }
    case 'digital_human':
      return {
        icon: <User className="h-5 w-5 text-spectrum-magenta" />,
        bgColor: 'bg-spectrum-magenta/10'
      }
    case 'vertical_ideas':
      return {
        icon: <BookOpen className="h-5 w-5 text-spectrum-emerald" />,
        bgColor: 'bg-spectrum-emerald/10'
      }
    case 'idea_research':
      return {
        icon: <Sparkles className="h-5 w-5 text-spectrum-lime" />,
        bgColor: 'bg-spectrum-lime/10'
      }
    default:
      return {
        icon: <Wand2 className="h-5 w-5 text-gray-600" />,
        bgColor: 'bg-gray-100'
      }
  }
}

export function TasksPage() {
  const queryClient = useQueryClient()
  const [searchParams] = useSearchParams()
  const highlightId = searchParams.get('highlight')

  const { data: tasks, isLoading } = useQuery({
    queryKey: ['tasks'],
    queryFn: () => api.getTasks({ limit: 50 }),
    // Only poll when there are active tasks
    refetchInterval: (query) => {
      const data = query.state.data
      const hasActiveTask = data?.items?.some(
        (task) => task.status === 'pending' || task.status === 'processing'
      )
      return hasActiveTask ? 5000 : false
    },
  })

  const cancelMutation = useMutation({
    mutationFn: (id: string) => api.cancelTask(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] })
      toast({ title: 'Task cancelled' })
    },
  })

  const retryMutation = useMutation({
    mutationFn: (id: string) => api.retryTask(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] })
      toast({ title: 'Task retried' })
    },
  })

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl border border-gray-200/60 shadow-sm">
        {isLoading ? (
          <div className="p-4 space-y-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="flex items-center gap-4 p-4">
                <div className="w-10 h-10 rounded-lg bg-gray-200/80 animate-pulse shrink-0" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-gray-200/80 animate-pulse rounded w-1/3" />
                  <div className="h-3 bg-gray-200/80 animate-pulse rounded w-2/3" />
                </div>
                <div className="w-20 h-6 bg-gray-200/80 animate-pulse rounded-full" />
              </div>
            ))}
          </div>
        ) : tasks?.items.length === 0 ? (
          <div className="p-12 text-center text-gray-500">
            No tasks yet. Start generating to see your tasks here.
          </div>
        ) : (
          <div className="divide-y">
            {tasks?.items.map((task) => (
              <div
                key={task.id}
                className={`p-4 flex items-center justify-between ${
                  highlightId === task.id ? 'bg-primary/5' : ''
                }`}
              >
                <div className="flex items-center gap-4">
                  {(() => {
                    const { icon, bgColor } = getTaskIconConfig(task.task_type)
                    return (
                      <div className={`w-10 h-10 rounded-lg ${bgColor} flex items-center justify-center`}>
                        {icon}
                      </div>
                    )
                  })()}
                  <div>
                    <div className="font-medium text-gray-900 capitalize">
                      {task.task_type.replace(/_/g, ' ')}
                    </div>
                    <div className="text-sm text-gray-500">
                      {task.success_count}/{task.requested_count} completed •{' '}
                      {formatDateTime(task.created_at)}
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  {task.credits_consumed > 0 && (
                    <span className="text-sm text-gray-500">
                      {task.credits_consumed} credit{task.credits_consumed !== 1 ? 's' : ''}
                    </span>
                  )}

                  {task.status === 'pending' && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => cancelMutation.mutate(task.id)}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  )}

                  {task.status === 'failed' && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => retryMutation.mutate(task.id)}
                    >
                      <RefreshCw className="h-4 w-4" />
                    </Button>
                  )}

                  <span className={`px-3 py-1 text-xs font-medium rounded-full ${getStatusColor(task.status)}`}>
                    {task.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
